import type { JSX } from "react";
import { useAutoResumePlayline } from "./useAutoResumePlayline";

/**
 * playline-persistence (Phase 5+ Tier 5E 第 10 段) client 配線:
 * reader が signed-in かつ cloud に再開可能な playline がある場合、
 * ScenarioStart の前に「前回の続きから読む」プロンプトを表示する。
 *
 * - anonymous / cloud 不在 / loading / error では何も出さず、 children (= ScenarioStart) を表示
 * - has-cloud-playline かつ reconcile が use-cloud のとき再開バナーを表示
 * - 「続きから読む」で onResume(playlineId, scenarioId)、「最初から」で dismiss して children 表示
 *
 * 既存 anonymous-first 経路は完全互換 (= 401 で即 children fallback)。
 */
export interface AutoResumePromptProps {
  baseUrl: string;
  onResume: (playlineId: string, scenarioId: string) => void;
  scenarioId?: string;
  requireScenarioMatch?: boolean;
  children: JSX.Element;
}

function formatTs(iso: string | null): string {
  if (iso === null) return "";
  try {
    return new Date(iso).toLocaleString("ja-JP", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function AutoResumePrompt({
  baseUrl,
  onResume,
  scenarioId,
  requireScenarioMatch = false,
  children,
}: AutoResumePromptProps): JSX.Element {
  const { state, latest, reconciled, dismiss } = useAutoResumePlayline(baseUrl);

  // loading 中は children を即表示 (= 既存挙動を blocking しない)。
  // anonymous / no-cloud / error も children fallback。
  // 「use-local」 reconcile (= client が新しい) も既存 localStorage 経路で十分なため children。
  const shouldPrompt =
    state === "has-cloud-playline" &&
    latest !== null &&
    reconciled !== null &&
    reconciled.kind === "use-cloud" &&
    (!requireScenarioMatch ||
      scenarioId === undefined ||
      latest.scenarioId === scenarioId);

  if (!shouldPrompt || latest === null) {
    return children;
  }

  const lastPlayed = formatTs(latest.lastPlayedAt);

  return (
    <div
      data-testid="auto-resume-prompt"
      className="flex min-h-screen flex-col items-center justify-center gap-6 bg-reader-bg p-6 text-center"
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-lg font-medium text-reader-primary">
          前回の続きから読みますか？
        </h1>
        <p className="text-sm text-reader-secondary">
          別の端末またはアカウントに保存された進捗があります。
          {lastPlayed !== "" && `（最終: ${lastPlayed}）`}
        </p>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="auto-resume-continue"
          onClick={() => onResume(latest.id, latest.scenarioId)}
          className="rounded bg-reader-brand px-5 py-2.5 text-sm font-medium text-white hover:bg-reader-brand-hover"
        >
          続きから読む
        </button>
        <button
          type="button"
          data-testid="auto-resume-dismiss"
          onClick={dismiss}
          className="rounded border border-reader-border px-5 py-2.5 text-sm text-reader-secondary hover:text-reader-primary"
        >
          最初から始める
        </button>
      </div>
    </div>
  );
}
